import React from "react";
import { LucideIcon } from "lucide-react";

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
}

export interface CustomButton {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger" | "success";
  disabled?: boolean;
  type?: "button" | "select";
  options?: { value: string; label: string }[];
  value?: string;
  onChange?: (value: string) => void;
}

export interface PageHeaderProps {
  // Header content
  icon: LucideIcon;
  title: string;
  description: string;

  // Tabs configuration (optional)
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;

  // Custom buttons (optional)
  customButtons?: CustomButton[];

  // Styling
  className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  description,
  tabs = [],
  activeTab = "",
  onTabChange = () => {},
  customButtons = [],
  className = "",
}) => {
  const getButtonStyles = (variant: string = "primary") => {
    const baseStyles =
      "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors";

    switch (variant) {
      case "primary":
        return `${baseStyles} text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:ring-[#264a3e]`;
      case "secondary":
        return `${baseStyles} text-[#264a3e] bg-white border-[#264a3e] hover:bg-gray-50 focus:ring-[#264a3e]`;
      case "danger":
        return `${baseStyles} text-white bg-red-600 hover:bg-red-700 focus:ring-red-500`;
      case "success":
        return `${baseStyles} text-white bg-green-600 hover:bg-green-700 focus:ring-green-500`;
      default:
        return `${baseStyles} text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:ring-[#264a3e]`;
    }
  };

  return (
    <div className={`${className} w-full`}>
      <div className="w-full lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className={tabs.length > 0 ? "mb-6 sm:mb-8" : ""}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center flex-wrap">
                <Icon className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-[#264a3e] flex-shrink-0" />
                <span className="break-words">{title}</span>
              </h1>
              <p className="hidden sm:block mt-2 text-sm sm:text-base text-gray-600">{description}</p>
            </div>

            {/* Custom Buttons */}
            {customButtons.length > 0 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:ml-6 flex-shrink-0">
                {customButtons.map((button) => {
                  const ButtonIcon = button.icon;

                  if (button.type === "select") {
                    // Find buttons that should be grouped with this select (only the immediate next ones)
                    const selectIndex = customButtons.findIndex(b => b.id === button.id);
                    const nextButtons = customButtons.slice(selectIndex + 1).filter(b => 
                      b.id === "add-order" || b.id === "bulk-import"
                    );

                    return (
                      <div key={button.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full sm:w-auto">
                        <label
                          htmlFor={button.id}
                          className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap"
                        >
                          {button.label}:
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            id={button.id}
                            value={button.value || ""}
                            onChange={(e) => button.onChange?.(e.target.value)}
                            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] text-sm"
                          >
                            {button.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {/* Add Order and Bulk Import buttons next to select */}
                          {nextButtons.map((nextButton) => {
                            const NextButtonIcon = nextButton.icon;
                            return (
                              <button
                                key={nextButton.id}
                                onClick={nextButton.onClick || (() => {})}
                                disabled={nextButton.disabled}
                                className={`${getButtonStyles(nextButton.variant)} justify-center touch-manipulation p-2 sm:px-3 sm:py-2`}
                                title={nextButton.label}
                              >
                                {NextButtonIcon && <NextButtonIcon className="w-4 h-4" />}
                                <span className="hidden">{nextButton.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  // For non-select buttons that are not add-order or bulk-import (they're handled above)
                  if (button.id === "add-order" || button.id === "bulk-import") {
                    return null; // These are handled in the select button's render
                  }

                  return (
                    <button
                      key={button.id}
                      onClick={button.onClick || (() => {})}
                      disabled={button.disabled}
                      className={`${getButtonStyles(button.variant)} w-full sm:w-auto justify-center touch-manipulation`}
                    >
                      {ButtonIcon && <ButtonIcon className="w-4 h-4 mr-2" />}
                      {button.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation - Only show if tabs exist */}
        {tabs.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden">
              <nav className="-mb-px flex space-x-2 sm:space-x-8 px-2 sm:px-6 min-w-max" aria-label="Tabs">
                {tabs.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? "border-[#264a3e] text-[#264a3e]"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      } py-2 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm flex flex-col items-center justify-center touch-manipulation transition-colors min-w-[60px] sm:min-w-0 sm:flex-row sm:whitespace-nowrap flex-shrink-0`}
                      title={tab.label}
                    >
                      <TabIcon className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-2 flex-shrink-0 mb-1 sm:mb-0" />
                      <span className="text-center leading-tight sm:leading-normal">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
